import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { api } from './api/client';
import { useAuth } from './auth/AuthProvider';
import { useStoredState } from './hooks';
import { rowsOf } from './search/globalSearch';

export interface LibraryOption {
  id: number;
  name: string;
  code?: string;
}

interface LibraryContextValue {
  libraries: LibraryOption[];
  current: LibraryOption | null;
  setCurrentId: (id: number) => void;
  loading: boolean;
}

const LibraryContext = createContext<LibraryContextValue | null>(null);

export function toLibraryOptions(payload: unknown): LibraryOption[] {
  return rowsOf(payload)
    .filter((r) => typeof r.id === 'number' && typeof r.name === 'string')
    .map((r) => ({ id: r.id as number, name: r.name as string, code: typeof r.code === 'string' ? r.code : undefined }));
}

/**
 * The branch the staff member is working at (persisted). Defaults to their home library.
 * Pages read `useLibrary().current?.id` to scope circulation, holds queues and reports.
 */
export function LibraryProvider({ children }: { children: ReactNode }) {
  const { user, status } = useAuth();
  const [storedId, setStoredId] = useStoredState<number | null>('lumina.library.v1', null);
  const query = useQuery({
    queryKey: ['libraries', 'switcher'],
    enabled: status === 'authenticated',
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: ({ signal }) => api.get<unknown>('/libraries', { query: { pageSize: 100 }, signal }).then(toLibraryOptions),
  });

  const libraries = useMemo<LibraryOption[]>(() => {
    const fetched = query.data ?? [];
    if (fetched.length > 0) {
      return fetched;
    }
    return user ? [{ id: user.homeLibraryId, name: user.homeLibraryName }] : [];
  }, [query.data, user]);

  const current = useMemo(() => libraries.find((l) => l.id === storedId) ?? libraries.find((l) => l.id === user?.homeLibraryId) ?? libraries[0] ?? null, [libraries, storedId, user]);
  const setCurrentId = useCallback((id: number) => setStoredId(id), [setStoredId]);

  const value = useMemo(() => ({ libraries, current, setCurrentId, loading: query.isLoading }), [libraries, current, setCurrentId, query.isLoading]);
  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary(): LibraryContextValue {
  const ctx = useContext(LibraryContext);
  if (!ctx) {
    return { libraries: [], current: null, setCurrentId: () => undefined, loading: false };
  }
  return ctx;
}
